import { ApolloCache } from '@apollo/client';

/**
 * Keep inbox unread UI in sync across:
 * - InboxEvent / Event.unreadMessageCount (row styling)
 * - inboxEvents.unreadCount on every cached connection (toolbar + nav badge)
 * - unread-only ("Hide Read") lists (drop marked-read events)
 */
export function markInboxEventsReadInCache(
  cache: ApolloCache<object>,
  eventIds: string[]
) {
  let newlyReadCount = 0;

  eventIds.forEach((eventId) => {
    const inboxEventCacheId = cache.identify({
      __typename: 'InboxEvent',
      id: eventId,
    });
    if (inboxEventCacheId) {
      cache.modify({
        id: inboxEventCacheId,
        fields: {
          unreadMessageCount(existing = 0) {
            if (existing > 0) {
              newlyReadCount += 1;
            }
            return 0;
          },
        },
      });
    }

    const eventCacheId = cache.identify({ __typename: 'Event', id: eventId });
    if (eventCacheId) {
      cache.modify({
        id: eventCacheId,
        fields: {
          unreadMessageCount: () => 0,
        },
      });
    }
  });

  if (newlyReadCount === 0) {
    return;
  }

  adjustInboxConnectionUnreadCounts(cache, -newlyReadCount, eventIds);
}

export function markInboxEventsUnreadInCache(
  cache: ApolloCache<object>,
  eventIds: string[]
) {
  let newlyUnreadCount = 0;

  eventIds.forEach((eventId) => {
    const inboxEventCacheId = cache.identify({
      __typename: 'InboxEvent',
      id: eventId,
    });
    if (inboxEventCacheId) {
      cache.modify({
        id: inboxEventCacheId,
        fields: {
          unreadMessageCount(existing = 0) {
            if (existing === 0) {
              newlyUnreadCount += 1;
            }
            return 1;
          },
        },
      });
    }

    const eventCacheId = cache.identify({ __typename: 'Event', id: eventId });
    if (eventCacheId) {
      cache.modify({
        id: eventCacheId,
        fields: {
          unreadMessageCount: () => 1,
        },
      });
    }
  });

  if (newlyUnreadCount === 0) {
    return;
  }

  adjustInboxConnectionUnreadCounts(cache, newlyUnreadCount);
}

export function markAllInboxEventsReadInCache(cache: ApolloCache<object>) {
  cache.modify({
    fields: {
      inboxEvents(existing, { readField }) {
        if (!existing) {
          return existing;
        }

        const edges = existing.edges;
        if (Array.isArray(edges)) {
          edges.forEach((edge: unknown) => {
            const node = readField('node', edge as object);
            const id = readField('id', node as object);
            if (typeof id === 'string') {
              const inboxEventCacheId = cache.identify({
                __typename: 'InboxEvent',
                id,
              });
              if (inboxEventCacheId) {
                cache.modify({
                  id: inboxEventCacheId,
                  fields: {
                    unreadMessageCount: () => 0,
                  },
                });
              }
            }
          });
        }

        return {
          ...existing,
          unreadCount: 0,
        };
      },
    },
  });
}

export function markAllInboxEventsUnreadInCache(cache: ApolloCache<object>) {
  cache.modify({
    fields: {
      inboxEvents(existing, { readField }) {
        if (!existing) {
          return existing;
        }

        const edges = existing.edges;
        const totalCount = readField<number>('totalCount', existing);
        if (Array.isArray(edges)) {
          edges.forEach((edge: unknown) => {
            const node = readField('node', edge as object);
            const id = readField('id', node as object);
            if (typeof id === 'string') {
              const inboxEventCacheId = cache.identify({
                __typename: 'InboxEvent',
                id,
              });
              if (inboxEventCacheId) {
                cache.modify({
                  id: inboxEventCacheId,
                  fields: {
                    unreadMessageCount: () => 1,
                  },
                });
              }
            }
          });
        }

        return {
          ...existing,
          unreadCount:
            typeof totalCount === 'number' ? totalCount : existing.unreadCount,
        };
      },
    },
  });
}

function adjustInboxConnectionUnreadCounts(
  cache: ApolloCache<object>,
  delta: number,
  removeEventIdsFromUnreadLists?: string[]
) {
  const removeIds = removeEventIdsFromUnreadLists
    ? new Set(removeEventIdsFromUnreadLists)
    : null;

  cache.modify({
    fields: {
      inboxEvents(existing, { readField, storeFieldName }) {
        if (!existing) {
          return existing;
        }

        const unreadCount = readField<number>('unreadCount', existing);
        let next = existing;

        if (typeof unreadCount === 'number') {
          next = {
            ...next,
            unreadCount: Math.max(0, unreadCount + delta),
          };
        }

        if (
          removeIds &&
          /"unread":\s*true/.test(storeFieldName) &&
          Array.isArray(existing.edges)
        ) {
          const filteredEdges = existing.edges.filter((edge: unknown) => {
            const node = readField('node', edge as object);
            const id = readField('id', node as object);
            return typeof id !== 'string' || !removeIds.has(id);
          });
          const removed = existing.edges.length - filteredEdges.length;
          if (removed > 0) {
            const totalCount = readField<number>('totalCount', existing);
            next = {
              ...next,
              edges: filteredEdges,
              totalCount:
                typeof totalCount === 'number'
                  ? Math.max(0, totalCount - removed)
                  : totalCount,
            };
          }
        }

        return next;
      },
    },
  });
}
